import { Table } from "antd"
import React from "react"

const SummaryRow = Table.Summary.Row
const SummaryCell = Table.Summary.Cell

type WithChildren = { children?: React.ReactNode }
type CellProps = WithChildren & { index?: number }

// `React.Children.toArray` flattens nested arrays but not fragments, so a row
// that hands its cells over as one `<>…</>` — the shape a ternary produces —
// arrives as a single opaque child. Treating that child as a cell would prepend
// the blank leading cell while silently skipping every index shift, which is
// the half-fix the doc comment below warns against. Descend instead.
const flattenCells = (node: React.ReactNode): React.ReactNode[] =>
  React.Children.toArray(node).flatMap((child) =>
    React.isValidElement(child) && child.type === React.Fragment
      ? flattenCells((child.props as WithChildren).children)
      : [child]
  )

/**
 * Re-aligns a totals row for the selection checkbox column.
 *
 * Two things are needed, not one. A blank leading cell keeps the columns
 * lining up visually — but rc-table also resolves each cell's sticky-fixed
 * info by looking `index` up in `flattenColumns`, and that list now includes
 * the checkbox column. Leaving the indices alone shifts every lookup one
 * column left, so a totals cell over a `fixed: "right"` column stops being
 * treated as fixed and scrolls away from the pinned header and body beneath
 * it. Each existing cell therefore moves up by one as well.
 *
 * `colSpan` is deliberately untouched: it describes how many data columns a
 * cell covers, which does not change when the row shifts.
 *
 * The shift is by exactly one, because the checkbox is the only column
 * EntityTable prepends to a table that has a summary. A table opting into both
 * `rowSortable` and `summary` would gain a grip column too and need a shift of
 * two — nothing does that today (Milestones is sortable but has no totals row),
 * so this stays hard-coded until something needs otherwise.
 */
export const shiftSummaryForSelection = (
  node: React.ReactNode
): React.ReactNode =>
  React.Children.map(node, (child) => {
    if (!React.isValidElement(child)) return child

    const props = child.props as WithChildren

    if (child.type === SummaryRow) {
      // Re-keyed by position: flattening merges cells from separate `toArray`
      // calls, each of which numbers its own output from `.0`, so the keys
      // would otherwise collide across a fragment boundary.
      const cells = flattenCells(props.children).map((cell, position) => {
        if (!React.isValidElement(cell)) return cell
        const key = `entity-table-cell-${position}`
        if (cell.type !== SummaryCell) return React.cloneElement(cell, { key })
        const cellProps = cell.props as CellProps
        return React.cloneElement(cell as React.ReactElement<CellProps>, {
          key,
          index: (cellProps.index ?? 0) + 1
        })
      })

      return React.cloneElement(
        child as React.ReactElement<WithChildren>,
        undefined,
        <SummaryCell key="entity-table-selection" index={0} />,
        ...cells
      )
    }

    // Never descend into a cell: its children are the consumer's own content,
    // and there are no summary rows to find in there.
    if (child.type === SummaryCell) return child

    if (props.children) {
      return React.cloneElement(
        child as React.ReactElement<WithChildren>,
        undefined,
        shiftSummaryForSelection(props.children)
      )
    }

    return child
  })
